import { apiClient } from '@/api/client';
import type { Comment, FeedItem, Interaction } from '@/types';

export interface FeedbackInput {
  contentId: string;
  reason: 'not_relevant' | 'already_seen' | 'poor_quality' | 'other';
  comment?: string;
}

export interface MuteInput {
  type: 'author' | 'source';
  value: string;
}

export interface SavedResponse {
  items: FeedItem[];
}

// Tipo da resposta do Hermes para bookmarks
interface HermesBookmark {
  id: string;
  content_id: string;
  created_at: string;
  title: string;
  summary: string | null;
  author: string | null;
}

// Converte resposta do Hermes para formato do frontend
function transformBookmarksResponse(bookmarks: HermesBookmark[], isLiked: boolean): SavedResponse {
  const items: FeedItem[] = bookmarks.map((bookmark) => ({
    content: {
      id: bookmark.content_id,
      title: bookmark.title,
      titleOriginal: bookmark.title,
      summary: bookmark.summary || '',
      theme: 'engineering' as const,
      author: bookmark.author || 'Unknown',
      source: 'Newsletter',
      sourceUrl: '',
      publishedAt: bookmark.created_at,
      createdAt: bookmark.created_at,
    },
    score: 1,
    tier: 'primary' as const,
    userState: {
      isLiked: isLiked,
      isSaved: !isLiked,
      isDismissed: false,
    },
  }));

  return { items };
}

export const interactionsService = {
  // Like/Favorite (Hermes: POST /bookmarks with type=FAVORITE)
  async like(contentId: string): Promise<Interaction> {
    return apiClient.post<Interaction>('/bookmarks', { contentId, type: 'FAVORITE' });
  },

  // Unlike (Hermes: DELETE /bookmarks/content/:contentId?type=FAVORITE)
  async unlike(contentId: string): Promise<void> {
    return apiClient.delete<void>(`/bookmarks/content/${contentId}?type=FAVORITE`);
  },

  // Save/Bookmark (Hermes: POST /bookmarks with type=BOOKMARK)
  async save(contentId: string): Promise<Interaction> {
    return apiClient.post<Interaction>('/bookmarks', { contentId, type: 'BOOKMARK' });
  },

  // Unsave (Hermes: DELETE /bookmarks/content/:contentId?type=BOOKMARK)
  async unsave(contentId: string): Promise<void> {
    return apiClient.delete<void>(`/bookmarks/content/${contentId}?type=BOOKMARK`);
  },

  // Dismiss content (Hermes: POST /interactions/dismiss)
  async dismiss(contentId: string): Promise<Interaction> {
    return apiClient.post<Interaction>('/interactions/dismiss', { contentId });
  },

  // Feedback (Hermes: POST /interactions/feedback)
  async feedback(data: FeedbackInput): Promise<Interaction> {
    return apiClient.post<Interaction>('/interactions/feedback', data);
  },

  // Mute author/source (Hermes: POST /interactions/mute/:type)
  async mute(data: MuteInput): Promise<void> {
    return apiClient.post<void>(`/interactions/mute/${data.type}`, { value: data.value });
  },

  // Mark as read (Hermes: POST /interactions/read)
  async markRead(contentId: string): Promise<Interaction> {
    return apiClient.post<Interaction>('/interactions/read', { contentId });
  },

  // Get saved items (Hermes: GET /bookmarks?type=BOOKMARK)
  async getSaved(): Promise<SavedResponse> {
    const bookmarks = await apiClient.get<HermesBookmark[]>('/bookmarks?type=BOOKMARK');
    return transformBookmarksResponse(bookmarks, false);
  },

  // Get liked/favorited items (Hermes: GET /bookmarks?type=FAVORITE)
  async getLiked(): Promise<SavedResponse> {
    const favorites = await apiClient.get<HermesBookmark[]>('/bookmarks?type=FAVORITE');
    return transformBookmarksResponse(favorites, true);
  },

  // Undo unsave - re-save the content (uses regular save endpoint)
  async undoUnsave(contentId: string): Promise<Interaction> {
    return apiClient.post<Interaction>('/bookmarks', { contentId, type: 'BOOKMARK' });
  },

  // Comments (Hermes: GET/POST /content/:contentId/comments)
  async getComments(contentId: string): Promise<Comment[]> {
    const response = await apiClient.get<
      Array<{
        id: string;
        content: string;
        created_at: string;
        updated_at: string;
        user_id: string;
        user_name: string;
      }>
    >(`/content/${contentId}/comments`);
    return response.map((c) => ({
      id: c.id,
      content: c.content,
      createdAt: c.created_at,
      updatedAt: c.updated_at,
      userId: c.user_id,
      userName: c.user_name,
    }));
  },

  async createComment(contentId: string, content: string): Promise<Comment> {
    const response = await apiClient.post<{
      id: string;
      content: string;
      created_at: string;
      updated_at: string;
      user_id: string;
    }>(`/content/${contentId}/comments`, { content });
    return {
      id: response.id,
      content: response.content,
      createdAt: response.created_at,
      updatedAt: response.updated_at,
      userId: response.user_id,
      userName: '',
    };
  },

  async updateComment(commentId: string, content: string): Promise<void> {
    await apiClient.put<void>(`/comments/${commentId}`, { content });
  },

  async deleteComment(commentId: string): Promise<void> {
    await apiClient.delete<void>(`/comments/${commentId}`);
  },
};
